// composeJoinPriceMinor — pure, deterministic composition of an activity-join charge amount (öre).
//
// 3f FEE-LED model (canonical). total = court_share + organizer_fee, all in MINOR units (öre).
//
// CRITICAL (2d-i Chrome MCP finding): the charge court MUST come from the SAME source the UI
// displays — calculateUniversalPrice's resolved court share (member-discounted + daily-limit
// adjusted, rounded to whole krona). This helper does NOT recompute court; it CONSUMES the
// already-resolved value (Lesson 38). apply_member_discount only chooses WHICH court applies:
//   true  → resolvedCourtShareKr (the player's category, = what the ActivityList card shows)
//   false → standardCourtShareKr (force standard rate for everyone; member discount disabled)
// The organizer fee is a FIXED add-on, never discounted.
//
// Court shares arrive already rounded to whole krona (calculateUniversalPrice rounds), so kr→öre
// is exact (× 100). The fee arrives already in öre.

export interface JoinPriceInput {
  /**
   * The player's RESOLVED court share, in kronor — exactly calculateUniversalPrice(...).courtShare,
   * the same value the ActivityList card / confirm dialog display. Member-discounted + daily-limit
   * adjusted. Used when the organizer allows the member discount (apply_member_discount = true).
   */
  resolvedCourtShareKr: number;
  /**
   * The STANDARD court share, in kronor (round(calculateBookingPrice('standard').total / players),
   * mirroring calculateUniversalPrice's own standard-category formula). Used only when the organizer
   * disables the member discount (apply_member_discount = false) — then everyone pays standard.
   */
  standardCourtShareKr: number;
  /** activities.apply_member_discount — true → resolved court, false → standard court. */
  applyMemberDiscount: boolean;
  /** activities.organizer_fee_per_person_minor — fixed öre add-on, never discounted. */
  organizerFeeMinor: number;
}

export interface JoinPriceResult {
  /** What this player owes, in öre. 0 → no charge and no booking_payments row written. */
  totalMinor: number;
  /** Court-rental portion this player owes, in öre. */
  courtShareMinor: number;
  /** Organizer fee portion, in öre (always = organizerFeeMinor; never discounted). */
  feeMinor: number;
  /** Whether the standard (non-discounted) court was charged (apply_member_discount = false). */
  usedStandardCourt: boolean;
}

/** kr → öre, exact for whole-krona court shares. Guards null/NaN. */
const krToMinor = (kr: number): number => {
  const n = Number(kr);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/**
 * Compose a single player's activity-join charge amount (fee-led, in öre).
 *
 * Examples (resolved court 113 kr [Sat], standard 113 kr [non-member], fee 50 kr = 5000 öre):
 * - apply_member_discount ON  → 113*100 + 5000 = 16300 öre (163 kr) — exactly the displayed price
 * - member resolved 100 kr, ON  → 100*100 + 5000 = 15000 öre (150 kr)
 * - member resolved 100 kr, OFF → standard 113 → 11300 + 5000 = 16300 öre (forces standard)
 * - resolved court 0 (100% rabatt) + fee 50 → 0 + 5000 = 5000 öre (charge only the fee)
 * - court 0 + fee 0 → 0 öre (no charge, no payment row)
 */
export function composeJoinPriceMinor(input: JoinPriceInput): JoinPriceResult {
  const { resolvedCourtShareKr, standardCourtShareKr, applyMemberDiscount, organizerFeeMinor } = input;

  const usedStandardCourt = !applyMemberDiscount;
  const courtKr = usedStandardCourt ? standardCourtShareKr : resolvedCourtShareKr;
  const courtShareMinor = Math.max(0, krToMinor(courtKr));

  const fee = Number(organizerFeeMinor);
  const feeMinor = Number.isFinite(fee) ? Math.max(0, Math.round(fee)) : 0;

  return {
    totalMinor: courtShareMinor + feeMinor,
    courtShareMinor,
    feeMinor,
    usedStandardCourt,
  };
}
